import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import getThemeColors from "../../utils/getColorTheme";
import HeaderPageBrad from "../../components/ComponentsPageBrad/HeaderPageBrad";
import HeaderPageLogin from "../../components/ComponentsPageBrad/HeaderPageLogin";
import { useIsMobile } from "../../utils/useIsMobile";
import { useTheme, ThemeProvider, ThemeContext } from "../../contexts/ThemeContext";
import { useUser } from "@supabase/auth-helpers-react";

// Local theme context that only provides light theme without affecting global state
const LocalLightThemeProvider = ({ children }) => {
  const lightThemeValue = {
    addTheme: "light",
    setAddTheme: () => {}, // No-op to prevent changes
    clearWithTheme: () => {} // No-op
  };

  return (
    <ThemeContext.Provider value={lightThemeValue}>
      {children}
    </ThemeContext.Provider>
  );
};

const TermsPage = () => {
  const { addTheme, setAddTheme } = useTheme();
  const { theme, background } = getThemeColors(addTheme);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const user = useUser();
  const isLoggedIn = !!user;

  // Force black text regardless of theme
  const textColor = "#16140E";
  
  useEffect(() => {
    // Ensure body allows scrolling
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, []);

  // Handle navigation functions
  const handleTermsAndConditionsClick = () => {
    navigate('/terms-and-conditions');
  };

  const handlePrivacyPolicyClick = () => {
    navigate('/privacy-policy');
  };

  const handleAOPromotionClick = () => {
    // For iOS Safari, we need to open in new tab since download doesn't work
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS) {
      window.open('/AOPromotion-01092025.pdf', '_blank');
    } else {
      const link = document.createElement('a');
      link.href = '/AOPromotion-01092025.pdf';
      link.download = 'AOPromotion-01092025.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

    return (
    <section className="section-main" style={{ 
      background: "#FFFFFF",
      height: "auto !important",
      minHeight: "100vh",
      overflow: "visible !important",
    }}>
      <div style={{ 
        background: "#FFFFFF", 
        minHeight: "100vh", 
        overflowY: "auto",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}>
      {/* Force light theme for header without affecting global theme */}
      <LocalLightThemeProvider>
        {isLoggedIn ? <HeaderPageBrad openModalLogin={""} /> : <HeaderPageLogin />}
      </LocalLightThemeProvider>
      
      {/* Main Content - with proper spacing from header */}
      <div style={{ paddingTop: isMobile ? "150px" : "100px", paddingBottom: "100px" }}>
        <div
          className="d-flex flex-column align-items-center"
          style={{
            width: "100%",
            maxWidth: 1440,
            padding: isMobile ? "0 16px" : "0",
            margin: "0 auto"
          }}
        >
          <div className="row justify-content-center">
            <div className="col-md-10 col-lg-7">
              <h1 
                className="text-center"
                style={{ 
                  fontSize: isMobile ? "42px" : "56px",
                  fontWeight: "bold",
                  marginBottom: "48px",
                  color: textColor
                }}
              >
                Terms and policies
              </h1>
              {/* Terms content */}
              <div className="mb-5">
                <p 
                  style={{ 
                    lineHeight: "1.6",
                    marginBottom: "24px",
                    marginLeft: "4px",
                    marginRight: "4px",
                    fontSize: "16px",
                    color: textColor
                  }}
                >
                  This Privacy Collection Notice describes how Provider Choice Shop Pty Ltd (ABN 22 678 898 265) trading as Fam Shop (we, us or our) collects and handles your personal information when you use our FAM assistant tool. We collect personal information from you so that we can help you understand your NDIS plan, recommend relevant products and services, personalise your experience, and for related purposes set out in our Privacy Policy, available on our website (or on request).
              </p>
              <p  style={{ 
                    lineHeight: "1.6",
                    marginBottom: "24px",
                    marginLeft: "4px",
                    marginRight: "4px",
                    fontSize: "16px",
                    color: textColor
                  }}>
                  We may disclose this personal information to third parties, including our personnel, related entities, IT service providers (including Customer.io in the United States), marketing providers, and any third parties engaged by us and acting on our behalf and as otherwise set out in our Privacy Policy.
              </p>
              <p style={{ 
                    lineHeight: "1.6",
                    marginBottom: "24px",
                    marginLeft: "4px",
                    marginRight: "4px",
                    fontSize: "16px",
                    color: textColor
                  }}>
                  We may store personal information overseas. Where we disclose your personal information to third parties, those third parties may also store, transfer or access personal information outside of Australia.
              </p>
              <p style={{ 
                    lineHeight: "1.6",
                    marginBottom: "24px",
                    marginLeft: "4px",
                    marginRight: "4px",
                    fontSize: "16px",
                    color: textColor
                  }}>
                  If you do not provide your personal information to us, it may affect our ability to provide personalised recommendations and services to you.
              </p>
              <p style={{ 
                    lineHeight: "1.6",
                    marginBottom: "24px",
                    marginLeft: "4px",
                    marginRight: "4px",
                    fontSize: "16px",
                    color: textColor
                  }}>
                  Please see our <a 
                    href="#"
                    onClick={handlePrivacyPolicyClick}
                    style={{ 
                      color: textColor,
                      cursor: "pointer"
                    }}
                  >Privacy Policy</a> for more information about how we collect, store, use and disclose your personal information, including details about overseas disclosure, access, correction, how you can make a privacy-related complaint and our complaint-handling process.
              </p>
              <p style={{ 
                    lineHeight: "1.6",
                    marginBottom: "24px",
                    marginLeft: "4px",
                    marginRight: "4px",
                    fontSize: "16px",
                    color: textColor
                  }}>
                  If you have questions about our privacy practices, please contact us by email at: <a 
                    href="#"
                    style={{ 
                      color: textColor,
                      cursor: "pointer"
                    }}>info@fam.life</a>. By providing your personal information to us, you agree to the collection, use, storage and disclosure of that information as described in this privacy collection notice.
              </p>
              </div>

              {/* Links section */}
              <div className="" style={{textDecoration: "underline" }}>
                <div className="d-flex align-items-center" style={{ marginTop: "24px", marginBottom: "8px" }}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.25 4.21875L16.75 9.46875C16.9062 9.625 17 9.8125 17 10.0312C17 10.2188 16.9062 10.4062 16.75 10.5625L11.25 15.8125C10.9688 16.0938 10.4688 16.0938 10.1875 15.7812C9.90625 15.5 9.90625 15 10.2188 14.7188L14.375 10.7812H3.75C3.3125 10.7812 3 10.4375 3 10.0312C3 9.59375 3.3125 9.28125 3.75 9.28125H14.375L10.2188 5.3125C9.90625 5.03125 9.90625 4.53125 10.1875 4.25C10.4688 3.9375 10.9375 3.9375 11.25 4.21875Z" fill={textColor}/>
                  </svg>
                  <a 
                    href="#" 
                    onClick={handleTermsAndConditionsClick}
                    style={{ 
                      fontWeight: "500",
                      fontSize: "18px",
                      marginLeft: "8px",
                      color: textColor,
                      cursor: "pointer"
                    }}
                  >
                    Terms and Conditions
                  </a>
                </div>
                <div className="d-flex align-items-center" style={{ marginTop: "8px", marginBottom: "8px"}}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.25 4.21875L16.75 9.46875C16.9062 9.625 17 9.8125 17 10.0312C17 10.2188 16.9062 10.4062 16.75 10.5625L11.25 15.8125C10.9688 16.0938 10.4688 16.0938 10.1875 15.7812C9.90625 15.5 9.90625 15 10.2188 14.7188L14.375 10.7812H3.75C3.3125 10.7812 3 10.4375 3 10.0312C3 9.59375 3.3125 9.28125 3.75 9.28125H14.375L10.2188 5.3125C9.90625 5.03125 9.90625 4.53125 10.1875 4.25C10.4688 3.9375 10.9375 3.9375 11.25 4.21875Z" fill={textColor}/>
                  </svg>
                  <a 
                    href="#"
                    onClick={handlePrivacyPolicyClick}
                    style={{ 
                      fontWeight: "500",
                      fontSize: "18px",
                      marginLeft: "8px",
                      color: textColor,
                      cursor: "pointer"
                    }}
                  >
                    Privacy Policy
                  </a>
                </div>
                <div className="d-flex align-items-center" style={{ marginTop: "8px", marginBottom: "24px"}}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.25 4.21875L16.75 9.46875C16.9062 9.625 17 9.8125 17 10.0312C17 10.2188 16.9062 10.4062 16.75 10.5625L11.25 15.8125C10.9688 16.0938 10.4688 16.0938 10.1875 15.7812C9.90625 15.5 9.90625 15 10.2188 14.7188L14.375 10.7812H3.75C3.3125 10.7812 3 10.4375 3 10.0312C3 9.59375 3.3125 9.28125 3.75 9.28125H14.375L10.2188 5.3125C9.90625 5.03125 9.90625 4.53125 10.1875 4.25C10.4688 3.9375 10.9375 3.9375 11.25 4.21875Z" fill={textColor}/>
                  </svg>
                  <a 
                    href="#"
                    onClick={handleAOPromotionClick}
                    style={{ 
                      fontWeight: "500",
                      fontSize: "18px",
                      marginLeft: "8px",
                      color: textColor,
                      cursor: "pointer"
                    }}
                  >
                    AO Promotion
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </section>
  );
};

export default TermsPage;